import { useEffect, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { HERO_VIDEOS, waLink } from "@/data/content";
import { JawaMark } from "./Logo";

const LINES = ["Your Return", "to Africa,", "Made Easier."];
const SLIDE_MS = 8000;

export const Hero = () => {
  const { scrollY } = useScroll();
  const videoY = useTransform(scrollY, [0, 900], [0, 220]);
  const fade = useTransform(scrollY, [0, 600], [1, 0]);

  const [slide, setSlide] = useState(0);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const fn = (e) => setReduced(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  useEffect(() => {
    if (reduced) return undefined;
    const t = setTimeout(() => setSlide((s) => (s + 1) % HERO_VIDEOS.length), SLIDE_MS);
    return () => clearTimeout(t);
  }, [slide, reduced]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [2.5, -2.5]), { stiffness: 60, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-2.5, 2.5]), { stiffness: 60, damping: 20 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative flex h-screen min-h-[640px] items-end overflow-hidden bg-jawa-nearblack"
    >
      <motion.div style={{ y: videoY }} className="absolute inset-0 scale-110" data-testid="hero-slideshow">
        {reduced ? (
          <img
            src={HERO_VIDEOS[0].poster}
            alt={HERO_VIDEOS[0].alt}
            className="absolute inset-0 h-full w-full object-cover"
            data-testid="hero-static-fallback"
          />
        ) : (
          <>
            <AnimatePresence mode="sync">
              <motion.div
                key={slide}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ opacity: { duration: 1.4, ease: "easeInOut" } }}
                className="absolute inset-0"
              >
                <video
                  ref={(v) => {
                    if (v) v.play().catch(() => {});
                  }}
                  className="h-full w-full object-cover"
                  style={slide === 0 ? { filter: "brightness(1.16) saturate(1.25) contrast(1.04) sepia(0.14)" } : undefined}
                  autoPlay
                  muted
                  playsInline
                  loop
                  preload="auto"
                  poster={HERO_VIDEOS[slide].poster}
                  aria-label={HERO_VIDEOS[slide].alt}
                  data-testid="hero-video"
                >
                  <source src={HERO_VIDEOS[slide].src} type="video/mp4" />
                </video>
              </motion.div>
            </AnimatePresence>
            <video
              src={HERO_VIDEOS[(slide + 1) % HERO_VIDEOS.length].src}
              preload="auto"
              muted
              className="hidden"
              aria-hidden="true"
            />
          </>
        )}
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-jawa-nearblack/85 via-jawa-nearblack/45 to-jawa-nearblack/40" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 lg:px-10">
        <motion.div style={{ rotateX, rotateY, transformPerspective: 900 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="mb-8"
          >
            <JawaMark className="h-14 w-14 text-jawa-ivory" />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-6 text-xs uppercase tracking-[0.4em] text-jawa-gold"
          >
            Lifestyle · Travel · Return · Invest
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-serif text-5xl font-medium leading-[1.02] tracking-tight text-jawa-ivory sm:text-6xl lg:text-7xl"
          >
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ delay: 0.35 + i * 0.16, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === 2 ? <em className="text-jawa-coral not-italic">{line}</em> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.9 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-jawa-ivory/80"
          >
            From airport arrivals and personalised concierge services to relocation guidance, investment resources,
            curated experiences and stays — JÀWÀ helps you navigate Nigeria with greater confidence.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="/services"
              data-testid="hero-explore-button"
              className="bg-jawa-coral px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-jawa-ivory hover:text-jawa-espresso"
            >
              Explore Our Services
            </a>
            <a
              href="/concierge"
              data-testid="hero-concierge-button"
              className="border border-jawa-turquoise px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-turquoise transition-colors duration-300 hover:bg-jawa-turquoise hover:text-jawa-nearblack"
            >
              Plan Your Visit
            </a>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-6 z-10 flex items-center gap-2 lg:left-10"
        data-testid="hero-slide-indicators"
      >
        {HERO_VIDEOS.map((s, i) => (
          <button
            key={s.alt}
            aria-label={`Show slide: ${s.alt}`}
            data-testid={`hero-indicator-${i}`}
            onClick={() => setSlide(i)}
            className={`h-0.5 transition-[width,background-color] duration-500 ${
              i === slide ? "w-10 bg-jawa-gold" : "w-5 bg-jawa-ivory/40 hover:bg-jawa-ivory/70"
            }`}
          />
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden items-center gap-3 text-jawa-ivory/60 md:flex"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          className="block h-10 w-px bg-jawa-ivory/50"
        />
      </motion.div>
    </section>
  );
};
